import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { discountCodesAPI, servicesAPI } from '../utils/api';
import { useTheme, useMediaQuery } from '@mui/material';

function AdminDiscountCodes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [viewingCode, setViewingCode] = useState(null);
  const [codeDetails, setCodeDetails] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: '',
    min_spend: '',
    category: '',
    expires_at: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [codesRes, servicesRes] = await Promise.all([
        discountCodesAPI.getAll(),
        servicesAPI.getAll()
      ]);
      setCodes(codesRes.data.codes || []);

      // Extract unique categories from services
      const cats = [...new Set(servicesRes.data.map(s => s.category).filter(Boolean))];
      setCategories(cats);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCode = async () => {
    try {
      const res = await discountCodesAPI.generate();
      setFormData(prev => ({ ...prev, code: res.data.code }));
    } catch (err) {
      toast.error('Failed to generate code');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        min_spend: formData.min_spend ? parseFloat(formData.min_spend) : 0,
        expires_at: formData.expires_at || null,
        category: formData.category || null
      };

      if (editingCode) {
        await discountCodesAPI.update(editingCode.id, payload);
        toast.success('Discount code updated');
      } else {
        await discountCodesAPI.create(payload);
        toast.success('Discount code created');
      }

      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save discount code');
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description || '',
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      max_uses: code.max_uses || '',
      min_spend: code.min_spend || '',
      category: code.category || '',
      expires_at: code.expires_at ? code.expires_at.split('T')[0] : ''
    });
    setShowForm(true);
  };

  const handleViewDetails = async (code) => {
    setViewingCode(code);
    try {
      const res = await discountCodesAPI.getById(code.id);
      setCodeDetails(res.data);
    } catch (err) {
      toast.error('Failed to load code details');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Are you sure you want to delete "${code.code}"?`)) return;

    try {
      await discountCodesAPI.delete(code.id);
      toast.success('Discount code deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete discount code');
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await discountCodesAPI.update(code.id, { active: !code.active });
      toast.success(`Code ${code.active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update code');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 10,
      max_uses: '',
      min_spend: '',
      category: '',
      expires_at: ''
    });
    setEditingCode(null);
    setShowForm(false);
  };

  const formatDiscount = (code) => {
    if (code.discount_type === 'percentage') {
      return `${code.discount_value}% off`;
    }
    return `£${code.discount_value} off`;
  };

  const isExpired = (code) => {
    if (!code.expires_at) return false;
    return new Date(code.expires_at) < new Date();
  };

  const isMaxedOut = (code) => {
    if (!code.max_uses) return false;
    return code.uses_count >= code.max_uses;
  };

  const getStatusBadge = (code) => {
    if (!code.active) {
      return <span className="badge badge-secondary">Inactive</span>;
    }
    if (isExpired(code)) {
      return <span className="badge badge-warning">Expired</span>;
    }
    if (isMaxedOut(code)) {
      return <span className="badge badge-warning">Max Uses Reached</span>;
    }
    return <span className="badge badge-success">Active</span>;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="spinner"></div>
        <p>Loading discount codes...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0 }}>Discount Codes</h1>
          <p style={{ color: '#666', margin: '0.25rem 0 0 0' }}>
            Create promotional codes for social media and marketing
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Create Code'}
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>{editingCode ? 'Edit Discount Code' : 'Create Discount Code'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Code</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SUMMER20"
                    style={{ flex: 1, textTransform: 'uppercase' }}
                    disabled={editingCode}
                  />
                  {!editingCode && (
                    <button type="button" className="btn btn-secondary" onClick={handleGenerateCode}>
                      Generate
                    </button>
                  )}
                </div>
                <small style={{ color: '#666' }}>Leave blank to auto-generate</small>
              </div>

              <div className="form-group">
                <label>Description (optional)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Summer Instagram promo"
                />
              </div>

              <div className="form-group">
                <label>Discount Type</label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (£)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Discount Value</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {formData.discount_type === 'fixed' && <span>£</span>}
                  <input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                    min="1"
                    max={formData.discount_type === 'percentage' ? 100 : 1000}
                    step={formData.discount_type === 'percentage' ? 1 : 0.5}
                    required
                    style={{ width: '100px' }}
                  />
                  {formData.discount_type === 'percentage' && <span>%</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Maximum Uses</label>
                <input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  min="1"
                />
                <small style={{ color: '#666' }}>Leave blank for unlimited</small>
              </div>

              <div className="form-group">
                <label>Minimum Spend (£)</label>
                <input
                  type="number"
                  value={formData.min_spend}
                  onChange={(e) => setFormData({ ...formData, min_spend: e.target.value })}
                  placeholder="No minimum"
                  min="0"
                  step="0.5"
                />
              </div>

              <div className="form-group">
                <label>Restrict to Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="">All Services</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Expiry Date</label>
                <input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
                <small style={{ color: '#666' }}>Leave blank for no expiry</small>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="submit" className="btn btn-primary">
                {editingCode ? 'Update Code' : 'Create Code'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Codes List */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Your Discount Codes</h3>

        {codes.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '2rem 0' }}>
            No discount codes yet. Create your first one!
          </p>
        ) : isMobile ? (
          /* Mobile Card View */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {codes.map(code => (
              <div
                key={code.id}
                style={{
                  background: code.active ? '#f8f9fa' : '#f0f0f0',
                  borderRadius: '12px',
                  padding: '1rem',
                  border: '1px solid #e0e0e0',
                  opacity: code.active ? 1 : 0.7
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <div>
                    <code style={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      background: '#8B2635',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px'
                    }}>
                      {code.code}
                    </code>
                    <div style={{ marginTop: '0.5rem' }}>
                      {getStatusBadge(code)}
                    </div>
                  </div>
                  <span style={{ fontSize: '1.25rem', fontWeight: 600, color: '#8B2635' }}>
                    {formatDiscount(code)}
                  </span>
                </div>

                {code.description && (
                  <p style={{ color: '#666', fontSize: '0.875rem', margin: '0.5rem 0' }}>
                    {code.description}
                  </p>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#666' }}>
                  <span>Uses: {code.uses_count}{code.max_uses ? `/${code.max_uses}` : ''}</span>
                  {code.min_spend > 0 && <span>| Min: £{code.min_spend}</span>}
                  {code.category && <span>| {code.category} only</span>}
                  {code.expires_at && <span>| Expires: {new Date(code.expires_at).toLocaleDateString()}</span>}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn btn-secondary"
                    style={{ flex: 1, padding: '0.5rem' }}
                    onClick={() => handleViewDetails(code)}
                  >
                    View
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ flex: 1, padding: '0.5rem' }}
                    onClick={() => handleEdit(code)}
                  >
                    Edit
                  </button>
                  <button
                    className={`btn ${code.active ? 'btn-warning' : 'btn-success'}`}
                    style={{ flex: 1, padding: '0.5rem' }}
                    onClick={() => handleToggleActive(code)}
                  >
                    {code.active ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Desktop Table View */
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Uses</th>
                  <th>Restrictions</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(code => (
                  <tr key={code.id} style={{ opacity: code.active ? 1 : 0.6 }}>
                    <td>
                      <code style={{
                        fontWeight: 600,
                        background: '#8B2635',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px'
                      }}>
                        {code.code}
                      </code>
                      {code.description && (
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                          {code.description}
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 600 }}>{formatDiscount(code)}</td>
                    <td>
                      {code.uses_count}{code.max_uses ? `/${code.max_uses}` : ' (unlimited)'}
                    </td>
                    <td>
                      {code.min_spend > 0 && <div>Min £{code.min_spend}</div>}
                      {code.category && <div>{code.category} only</div>}
                      {!code.min_spend && !code.category && <span style={{ color: '#999' }}>None</span>}
                    </td>
                    <td>
                      {code.expires_at
                        ? new Date(code.expires_at).toLocaleDateString()
                        : <span style={{ color: '#999' }}>Never</span>
                      }
                    </td>
                    <td>{getStatusBadge(code)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem' }}
                          onClick={() => handleViewDetails(code)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem' }}
                          onClick={() => handleEdit(code)}
                        >
                          Edit
                        </button>
                        <button
                          className={`btn ${code.active ? 'btn-warning' : 'btn-success'}`}
                          style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem' }}
                          onClick={() => handleToggleActive(code)}
                        >
                          {code.active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem' }}
                          onClick={() => handleDelete(code)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Code Details Modal */}
      {viewingCode && codeDetails && (
        <div className="modal-overlay" onClick={() => { setViewingCode(null); setCodeDetails(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>
                <code style={{ background: '#8B2635', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '4px' }}>
                  {codeDetails.code.code}
                </code>
              </h2>
              <button className="modal-close-btn" onClick={() => { setViewingCode(null); setCodeDetails(null); }}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <div style={{ color: '#666', fontSize: '0.8rem' }}>Discount</div>
                  <div style={{ fontWeight: 600, fontSize: '1.25rem' }}>{formatDiscount(codeDetails.code)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.8rem' }}>Status</div>
                  <div>{getStatusBadge(codeDetails.code)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.8rem' }}>Uses</div>
                  <div style={{ fontWeight: 600 }}>
                    {codeDetails.code.uses_count}{codeDetails.code.max_uses ? ` / ${codeDetails.code.max_uses}` : ''}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.8rem' }}>Total Savings Given</div>
                  <div style={{ fontWeight: 600 }}>
                    £{codeDetails.uses.reduce((sum, u) => sum + (u.discount_amount || 0), 0).toFixed(2)}
                  </div>
                </div>
              </div>

              <h4 style={{ marginTop: 0 }}>Usage History</h4>
              {codeDetails.uses.length === 0 ? (
                <p style={{ color: '#666', textAlign: 'center', padding: '1rem 0' }}>
                  This code hasn't been used yet.
                </p>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <table className="data-table" style={{ fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Service</th>
                        <th>Saved</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codeDetails.uses.map(use => (
                        <tr key={use.id}>
                          <td>{use.customer_name || 'Guest'}</td>
                          <td>{use.service_name || '-'}</td>
                          <td>£{use.discount_amount?.toFixed(2)}</td>
                          <td>{new Date(use.used_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setViewingCode(null); setCodeDetails(null); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="card" style={{ marginTop: '1.5rem', background: '#f8f9fa' }}>
        <h4 style={{ marginTop: 0 }}>Tips for Promotional Codes</h4>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#666' }}>
          <li>Use memorable codes like <code>SUMMER20</code> or <code>NEWCLIENT</code></li>
          <li>Set a maximum uses limit to create urgency ("first 50 customers only!")</li>
          <li>Add an expiry date for time-limited promotions</li>
          <li>Restrict to categories to promote specific services</li>
          <li>Share codes on Instagram stories for flash sales</li>
        </ul>
      </div>
    </div>
  );
}

export default AdminDiscountCodes;
